import { error } from '@/utils/logging'
import $ from 'jquery'
import _ from 'underscore'
import { NotFoundError } from '@/api/error'
import { trackRestoredProofError } from '@/services/analytics'
import { ItemQuantitySyncBehavior, SurfaceObject } from '@/api/items'

interface JQueryApiResponse {
  data: { [key: string]: unknown }
  textStatus: string
  jqXHR: JQuery.jqXHR
}

/**
 * Deletes the specified proofs.
 */
export function deleteProofsRequest(
  proofIds: string[]
): Promise<JQueryApiResponse> {
  return new Promise((resolve, reject) => {
    $.ajax('/eco/api/v1/proofs', {
      method: 'DELETE',
      data: JSON.stringify(proofIds),
      contentType: 'application/json',
      authenticate: true,
      timeout: 30 * 1000
    } as JQuery.AjaxSettings)
      .always(() => {
        proofIds.forEach(clearProofDataCacheByProofId)
      })
      .done(function (data, textStatus, jqXHR) {
        resolve({ data, textStatus, jqXHR })
      })
      .fail((err) => {
        error('pcCart.deleteProofs() failed', {
          proofIds
        })
        reject(err)
      })
  })
}

/**
 * Cancels the specified proof.
 */
export function cancelProofRequest(
  proofId: string
): Promise<JQueryApiResponse> {
  return new Promise((resolve, reject) => {
    $.ajax({
      url: '/eco/proof-approval/submit',
      type: 'post',
      data: {
        proofId,
        // Cancel the current best revision
        revisionNumber: -1,
        approval: 'cancelled',
        approvalEditRequests: ''
      },
      timeout: 30 * 1000
    })
      .always(() => {
        clearProofDataCacheByProofId(proofId)
      })
      .done(function (data, textStatus, jqXHR) {
        resolve({ data, textStatus, jqXHR })
      })
      .fail((err) => {
        error('Cancel proof failed', {
          proofId
        })
        reject(err)
      })
  })
}

export type ProofSurface = {
  name: string
  index: number
  width: number
  height: number
  url: string
  overlayUrls: string[]
  maskUrl: string
}

/**
 * Personalized proof metadata and related item data.
 *
 * The proof data can be used to understand the current state of a personalized proof.
 * The item, format, and surface data include a subset of data from the items API
 * to help the frontend avoid additional API requests for performance.
 */
type ProofDatumTemplate<T> = {
  surfaces: ProofSurface[]
  reorder: boolean
  editsInProgress: boolean
  awaitingApproval: boolean
  saved: boolean
  approved: boolean
  formatName: string
  created: number
  projectId: string
  quantitySyncBehavior: ItemQuantitySyncBehavior | null
  quantitySyncFulfillmentAttribute: 'extraPageCount' | null
  attributes: {
    varData: { [key: string]: string }
    fulfillment: T
  }
}

type StringFulfillmentAttributes = {
  addOnsFlowCompleted?: string
  hasBeenAddedToCart?: string
  substrate?: string
  trim?: string
  extraPageCount?: string
}

export type NativeFulfillmentAttributes = Omit<
  StringFulfillmentAttributes,
  'addOnsFlowCompleted' | 'hasBeenAddedToCart' | 'extraPageCount'
> & {
  // `true` is implied if `undefined` and the app logic is expected to remove the value unless `false`
  addOnsFlowCompleted?: false
  // `true` is implied if `undefined` and the app logic is expected to remove the value unless `false`
  hasBeenAddedToCart?: false
  extraPageCount?: number
}

export type VarDataAddress = {
  line1?: string
  line2?: string
  line3?: string
  line4?: string
  line5?: string
  line6?: string
}

export type OrderThankYou = {
  text: string
}

export type TreeDedication = {
  text: string
  dedicatedBy?: string
  dedicatedTo?: string
}

export enum ForceEspeciallyFor {
  Default = 'DEFAULT',
  Always = 'ALWAYS',
  Never = 'NEVER'
}

export type NativeVarDataAttributes = {
  returnAddress?: VarDataAddress
  recipientAddress?: VarDataAddress
  orderThankYou?: OrderThankYou
  treeDedication?: TreeDedication
  sortCode?: string
  designerName?: string
  forceEspeciallyFor?: ForceEspeciallyFor
  objects?: { [objectId: string]: Partial<SurfaceObject> }
}

/**
 * Proof data with attribute types cast to appropriate types (e.g. number instead of string).
 */
export type ProofDatum = ProofDatumTemplate<NativeFulfillmentAttributes>

export type ProofData = {
  [proofId: string]: ProofDatum
}

/**
 * Proof data with string attribute types.
 * The API always has string values, which will then be cast to the appropriate types internally for `ProofDatum`.
 */
export type ProofDatumApiResponse =
  ProofDatumTemplate<StringFulfillmentAttributes>

type ProofDataFromApi = {
  [proofId: string]: ProofDatumApiResponse
}

const proofDataCache: { [proofId: string]: Promise<ProofDatum | null> } = {}

/**
 * Clears proof data cache related to a specific proof ID.
 *
 * If fresh data is necessary, this is preferred (for performance) versus clearing the entire cache.
 * Keep in mind that this has no awareness of parent/child relationships.
 */
export const clearProofDataCacheByProofId = (proofId: string): void => {
  delete proofDataCache[proofId]
}

/**
 * Clears the entire proof data cache.
 *
 * If `clearProofDataCacheByProofId` can be used safely instead, that will ensure better performance by retaining
 * as much cached data as possible.
 */
export const clearProofDataCache = (): void => {
  for (const proofId in proofDataCache) {
    clearProofDataCacheByProofId(proofId)
  }
}

const castFulfillmentAttributesToNative = (
  attributes: StringFulfillmentAttributes
): NativeFulfillmentAttributes => ({
  ...attributes,
  addOnsFlowCompleted:
    attributes.addOnsFlowCompleted === 'false' ? false : undefined,
  hasBeenAddedToCart:
    attributes.hasBeenAddedToCart === 'false' ? false : undefined,
  extraPageCount:
    typeof attributes.extraPageCount === 'string'
      ? Number(attributes.extraPageCount)
      : undefined
})

// TODO: Use for proof API, and remove eslint-disable-next-line
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const castAttributesToStrings = (attributes: {
  [key: string]: unknown
}): { [key: string]: string | undefined } =>
  _.mapObject(attributes, (attribute) =>
    attribute !== undefined ? String(attribute) : undefined
  )

/**
 * Converts string fulfillment attribute values to native data types.
 */
const castProofDatumApiResponse = (data: ProofDatumApiResponse): ProofDatum => {
  // Deep clone via spread preserves type safety and immutability
  return {
    ...data,
    attributes: {
      ...data.attributes,
      fulfillment: castFulfillmentAttributesToNative(
        data.attributes.fulfillment
      )
    }
  }
}

/**
 * Fetches proof data, attributes, and item and format data for the given proof IDs.
 */
export function fetchProofData(...proofIds: string[]): Promise<ProofData> {
  if (!proofIds.length) {
    return Promise.resolve({})
  }
  return new Promise((resolve, reject) => {
    $.ajax({
      type: 'post',
      url: '/eco/proof/urls',
      data: {
        ids: proofIds.join(',')
      }
    })
      .done((data: ProofDataFromApi) => {
        resolve(_.mapObject(data, castProofDatumApiResponse))
      })
      .fail((e) => {
        error('Proof URLs failed', {
          proofIds,
          error: e
        })
        reject(e)
      })
  })
}

/**
 * Partitions an array of proof IDs into an array of Promises with a proof ID and result,
 * and an array of uncached proof IDs.
 */
const partitionCachedRequests = (
  proofIds: string[]
): [Promise<[string, ProofDatum | null]>[], string[]] => {
  // Split proofIds into cached cachedRequests and uncachedProofIds
  const cachedRequests: Promise<[string, ProofDatum | null]>[] = []
  const uncachedProofIds: string[] = []
  proofIds.forEach((proofId) => {
    const cachedRequest = proofDataCache[proofId]
    if (!cachedRequest) {
      uncachedProofIds.push(proofId)
    } else {
      cachedRequests.push(cachedRequest.then((data) => [proofId, data]))
    }
  })
  return [cachedRequests, uncachedProofIds]
}

/**
 * Returns proof data, attributes, and item and format data for the given proof IDs.
 *
 * Results are cached and cached data will be returned if available.
 */
export const proofData = async (...proofIds: string[]): Promise<ProofData> => {
  if (!proofIds.length) {
    return {}
  }

  const [cachedRequests, uncachedProofIds] = partitionCachedRequests(proofIds)
  try {
    const proofDataPromise = fetchProofData(...uncachedProofIds)
    // Cache requests immediately to prevent duplicate concurrent requests for a proof ID
    uncachedProofIds.forEach(
      (proofId) =>
        (proofDataCache[proofId] = proofDataPromise.then(
          (data) => data[proofId] ?? null
        ))
    )

    const result: ProofData = {
      ...(await proofDataPromise)
    }
    // Cached requests include cached requests in flight. This prevents concurrent duplicate requests.
    const cachedResults = await Promise.all(cachedRequests)
    cachedResults.forEach(([proofId, data]) => {
      if (data) {
        result[proofId] = data
      }
    })
    return result
  } catch (error) {
    // Clear failed request from cache, so it will be retried next time
    uncachedProofIds.forEach(clearProofDataCacheByProofId)

    throw error
  }
}

interface ReorderProofRequestData {
  originalProofId: string
  targetProofId: string
  email?: string
  orderId?: string
  orderItemId?: number
  itemId?: number
}

/** Cleaned up data returned by the reorder proof service on success. */
interface ReorderProofResponseData {
  proofStatus: string
}

/** Raw data returned by the API. */
interface ReorderProofResponseRaw {
  success: boolean
  result?: {
    success: 'true' | 'false'
    code: 'SUCCESS' | 'NOT_FOUND' | 'EXCEPTION'
    message?: string
    // TODO: Add ProofStatus type with possible statuses
    proofStatus?: string
    method: string
    version: string
  }
  error?: string
}

/**
 * Creates a new proof as a reordered proof.
 *
 * The ordered proof with `targetProofId` will act as a symlink-like reference to the original proof
 * with `originalProofId`.
 * This will set up personalization-related data on the backend for the reorder, including attributes and proof objects.
 * The frontend is responsible for all related cart updates, and add-ons will still need to be configured for the reorder.
 *
 * TODO: Reorder should inherit all add-ons and customizations
 * TODO: Once backend support is added, rewrite to allow reused of the same customizationId from the originalProofId so symlink-behavior isn't necessary
 */
export function reorderProof({
  targetProofId,
  ...params
}: ReorderProofRequestData): Promise<ReorderProofResponseData> {
  return new Promise((resolve, reject) => {
    $.ajax({
      type: 'post',
      url: '/eco/proof/reorder',
      data: {
        targetProofId,
        ...params
      }
    })
      .always(() => {
        clearProofDataCacheByProofId(targetProofId)
        if (params.originalProofId) {
          clearProofDataCacheByProofId(params.originalProofId)
        }
      })
      .then((response: ReorderProofResponseRaw) => {
        if (!response.success || !response.result) {
          reject(new Error(response.error || 'Server error creating reorder'))
        } else if (response.result.success !== 'true') {
          if (response.result.code === 'NOT_FOUND') {
            reject(
              new NotFoundError(response.result.message || 'Proof not found')
            )
          } else if (response.result.code === 'EXCEPTION') {
            reject(new Error('Reorder creation failed'))
          } else {
            reject(
              new Error(
                `Server error creating reorder: ${response.result.message}`
              )
            )
          }
        } else {
          if (response.result.proofStatus) {
            resolve({
              proofStatus: response.result.proofStatus
            })
          } else {
            // Handle the undefined case - reject or provide a default value
            reject(new Error('Proof status is undefined'))
            // Or provide a default value, e.g., resolve({ proofStatus: 'defaultStatus' });
          }
        }
      })
      .fail(reject)
  })
}

interface PersonalizeUpdateRequestData {
  proofId: string
  addOnIds: number[]
  varDataAttributes: { [key: string]: unknown }
  fulfillmentAttributes: StringFulfillmentAttributes
}

type PersonalizeUpdateResult = {
  delete: string[]
  upsert: { [itemId: number]: string }
}

interface PersonalizeUpdateResponse {
  success: boolean
  message?: string
  result: PersonalizeUpdateResult
}

/**
 * Updates add-ons, `varDataAttributes`, and `fulfillmentAttributes` for a proof.
 *
 * @returns Data for client-initiated cart operations: proof IDs for each add-on item ID to upsert, proof IDs to delete.
 */
export const personalizeUpdate = (
  requestData: PersonalizeUpdateRequestData
): Promise<PersonalizeUpdateResult> => {
  return new Promise((resolve, reject) => {
    $.ajax({
      type: 'post',
      url: '/eco/personalize/update',
      dataType: 'json',
      contentType: 'application/json',
      data: JSON.stringify(requestData)
    })
      .always(() => {
        clearProofDataCacheByProofId(requestData.proofId)
      })
      .done(function (data: PersonalizeUpdateResponse, textStatus, jqXHR) {
        if (!data.success) {
          error('/eco/personalize/update failed', {
            response: data,
            textStatus,
            jqXHR,
            postData: requestData
          })
          reject(new Error(`Error saving proof attributes: ${jqXHR.status}`))
        } else {
          resolve(data.result)
        }
      })
      .fail(function (jqXHR, textStatus, errorThrown) {
        reject(
          new Error(
            `Error saving proof attributes: HTTP ${textStatus} ${jqXHR.status} ${errorThrown}`
          )
        )
      })
  })
}

type RestoreResponseResult = {
  editable: boolean
  itemId: number
  newBack: boolean
  personalizeState: string
  fulfillmentAttributes: { [key: string]: string }
  varDataAttributes: { [key: string]: string }
}

type RestoreResponseSuccess = {
  success: true
  result: RestoreResponseResult
}

type RestoreResponseError = {
  success: false
  error?: string
}

type RestoreResponseData = RestoreResponseSuccess | RestoreResponseError

const restoreProofByIdRequest = (
  proofId: string,
  authenticate: boolean = false
): Promise<RestoreResponseData> => {
  return new Promise((resolve, reject) => {
    $.ajax({
      type: 'post',
      url: '/eco/proof/restore',
      data: { proofId },
      dataType: 'json',
      authenticate
    })
      .then(resolve)
      .fail(reject)
  })
}

export const restoreProofById = async (
  proofId: string,
  authenticate: boolean = false
): Promise<RestoreResponseResult | null> => {
  const restoreData = await restoreProofByIdRequest(proofId, authenticate)
  if (restoreData.success) {
    return restoreData.result
  } else {
    const error = restoreData.error || ''
    const noProofDataFound = error.startsWith(
      "Can't find any revisions for proof ID"
    )
    // Error out of here, unless we found no proof data at all, in which case we'll load a blank personalize state.
    if (error && !noProofDataFound) {
      // TODO: Add analytics context, which isn't currently available from here
      trackRestoredProofError({
        message: restoreData.error || null
      })
      throw new Error('Request to /eco/proof/restore failed')
    }
    return null
  }
}

const upsertProofRequest = (requestData: {
  id: string
  itemId: number
  fulfillmentAttributes: StringFulfillmentAttributes
}): Promise<
  | {
      cartItems: CartItemsFromProof[]
      fulfillmentAttributes: {
        attributes: StringFulfillmentAttributes
      }
    } // TODO: Once Mondrian #356 surface-object updates are deployed, remove the above legacy adapter type
  | {
      cartItems: CartItemsFromProof[]
      fulfillmentAttributes: NativeFulfillmentAttributes
    }
> => {
  return new Promise((resolve, reject) => {
    $.ajax({
      type: 'put',
      url: '/api/v2/proofs',
      data: JSON.stringify(requestData),
      contentType: 'application/json',
      authenticate: true
    })
      .always(() => clearProofDataCacheByProofId(requestData.id))
      .then(resolve, reject)
  })
}

type CartItemsFromProof = {
  itemId: number
  proofId: string
  quantitySyncBehavior: ItemQuantitySyncBehavior
  quantitySyncFulfillmentAttribute: string
}

export const upsertProof = async (proof: {
  id: string
  itemId: number
  fulfillmentAttributes: NativeFulfillmentAttributes
}): Promise<{
  cartItems: CartItemsFromProof[]
  fulfillmentAttributes: NativeFulfillmentAttributes
}> => {
  const response = await upsertProofRequest({
    id: proof.id,
    itemId: proof.itemId,
    // TODO: Once Mondrian #356 surface-object updates are deployed, remove string casting and send native types
    fulfillmentAttributes: castAttributesToStrings(proof.fulfillmentAttributes)
  })

  return {
    ...response,
    // TODO: Once Mondrian #356 surface-object updates are deployed, remove the above legacy adapter
    fulfillmentAttributes:
      'attributes' in response.fulfillmentAttributes
        ? castFulfillmentAttributesToNative(
            response.fulfillmentAttributes.attributes
          )
        : response.fulfillmentAttributes
  }
}

export const duplicateProofRequest = (
  proofId: string
): Promise<
  | {
      cartItems: CartItemsFromProof[]
      fulfillmentAttributes: NativeFulfillmentAttributes
    }
  | {
      // TODO: Once Mondrian #356 surface-object updates are deployed, remove the above legacy adapter type
      cartItems: CartItemsFromProof[]
      fulfillmentAttributes: {
        attributes: StringFulfillmentAttributes
      }
    }
> => {
  return new Promise((resolve, reject) => {
    $.ajax({
      type: 'post',
      url: `/api/v2/proofs/${proofId}/clone`,
      authenticate: true
    })
      .then(resolve)
      .catch((err) => {
        error('Failed to duplicate proof', {
          proofId
        })
        reject(err)
      })
  })
}

/**
 * Duplicate the proof, project, add-ons, and fulfillment attributes.
 */
export const duplicateProof = async (
  proofId: string
): Promise<{
  cartItems: CartItemsFromProof[]
  fulfillmentAttributes: NativeFulfillmentAttributes
}> => {
  const response = await duplicateProofRequest(proofId)

  return {
    ...response,
    fulfillmentAttributes:
      'attributes' in response.fulfillmentAttributes
        ? castFulfillmentAttributesToNative(
            response.fulfillmentAttributes.attributes
          )
        : // TODO: Once Mondrian #356 surface-object updates are deployed, remove the above legacy adapter
          response.fulfillmentAttributes
  }
}
